import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { AppShellProvider } from '@/app/shell/AppShellContext';
import { resolveNavigationItem } from '@/app/shell/navigationManifest';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { BottomNav } from '@/components/navigation/BottomNav';
import { GlobalFab } from '@/components/navigation/GlobalFab';
import { SideDrawer } from '@/components/navigation/SideDrawer';
import { useClinicProfile } from '@/features/owner/api/ownerApi';
import { OfflineBanner } from '@/features/shared/components/OfflineBanner';
import { StandaloneHintBanner } from '@/features/shared/components/StandaloneHintBanner';
import { useAppDensityMode } from '@/features/shared/hooks/useAppDensityMode';
import { useAppExperienceMode } from '@/features/shared/hooks/useAppExperienceMode';
import { appDensityModeClassName } from '@/features/shared/lib/appDensityMode';
import { appExperienceModeClassName } from '@/features/shared/lib/appExperienceMode';
import { cn } from '@/utils/cn';

export function AppLayout() {
  const location = useLocation();
  const routeMeta = resolveNavigationItem(location.pathname);
  const clinicProfileQuery = useClinicProfile();
  const hideShellChrome = routeMeta.chromeMode === 'immersive';
  const clinicName = clinicProfileQuery.data?.clinicName ?? 'Clinic workspace';
  const logoUrl = clinicProfileQuery.data?.logoUrl ?? null;
  const experienceMode = useAppExperienceMode();
  const densityMode = useAppDensityMode();
  const [isStandalone, setIsStandalone] = useState(() => {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
  });

  useEffect(() => {
    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    const nav = window.navigator as Navigator & { standalone?: boolean };
    const onDisplayModeChange = () => {
      setIsStandalone(displayModeQuery.matches || nav.standalone === true);
    };

    displayModeQuery.addEventListener('change', onDisplayModeChange);
    return () => displayModeQuery.removeEventListener('change', onDisplayModeChange);
  }, []);

  return (
    <AppShellProvider value={{ clinicName, logoUrl }}>
      <div
        className={cn(
          'min-h-dvh bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50',
          appExperienceModeClassName(experienceMode),
          appDensityModeClassName(densityMode),
          isStandalone ? 'app-safe-top' : ''
        )}
      >
        <OfflineBanner />
        <StandaloneHintBanner />
        {hideShellChrome ? (
          <main className="w-full">
            <Outlet />
          </main>
        ) : (
          <>
            <div className="relative lg:flex lg:min-h-dvh">
              <Sidebar />
              <div id="app-shell-content" className="flex min-h-dvh flex-1 flex-col">
                <div className="hidden lg:block">
                  <Header />
                </div>
                <main className="w-full flex-1">
                  <Outlet />
                </main>
              </div>
            </div>
            <SideDrawer />
            {routeMeta.showGlobalFab ? <GlobalFab /> : null}
            {routeMeta.showBottomNav ? <BottomNav /> : null}
          </>
        )}
      </div>
    </AppShellProvider>
  );
}
